import React from 'react';
import { ContactRowBlock as ContactRowBlockType } from './types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

const iconOptions = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'website', label: 'Website' },
  { value: 'location', label: 'Location' },
];

// Map icon type to actual PNG icon path (same as email output for WYSIWYG)
const getContactIconPath = (iconType: string): string => {
  const iconMap: Record<string, string> = {
    email: '/icons/contact/email.png',
    phone: '/icons/contact/phone.png',
    whatsapp: '/icons/contact/whatsapp.png',
    website: '/icons/contact/website.png',
    location: '/icons/contact/location.png',
  };
  return iconMap[iconType] || '/icons/contact/email.png';
};

const dynamicFields = [
  { label: 'Your Email', value: '{{user_email}}' },
  { label: 'Your Phone', value: '{{user_phone}}' },
  { label: 'Company Email', value: '{{company_email}}' },
  { label: 'Company Phone', value: '{{company_phone}}' },
  { label: 'Company Website', value: '{{company_website}}' },
];

interface ContactRowBlockProps {
  block: ContactRowBlockType;
  onChange: (block: ContactRowBlockType) => void;
  isEditing?: boolean;
}

export const ContactRowBlockDisplay: React.FC<{ block: ContactRowBlockType; processedLabel?: string }> = ({ 
  block,
  processedLabel 
}) => {
  const iconPath = getContactIconPath(block.icon);
  const label = processedLabel || block.label;

  if (!label) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground/50 italic">
        <img 
          src={iconPath} 
          alt={block.icon}
          width={block.iconSize} 
          height={block.iconSize}
          style={{ display: 'inline-block', flexShrink: 0 }}
        />
        <span style={{ fontSize: block.fontSize }}>Enter contact info...</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <img 
        src={iconPath} 
        alt={block.icon}
        width={block.iconSize} 
        height={block.iconSize}
        style={{ display: 'inline-block', flexShrink: 0 }}
      />
      <span 
        style={{ 
          fontSize: block.fontSize,
          color: block.textColor,
        }}
      >
        {label}
      </span>
    </div>
  );
};

export const ContactRowBlockEditor: React.FC<ContactRowBlockProps> = ({ block, onChange }) => {
  const handleInsertField = (fieldValue: string) => {
    onChange({ ...block, label: block.label + fieldValue });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Icon Type</Label>
        <Select
          value={block.icon}
          onValueChange={(value: ContactRowBlockType['icon']) => onChange({ ...block, icon: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {iconOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <div className="flex items-center gap-2">
                  <img 
                    src={getContactIconPath(option.value)} 
                    alt={option.label}
                    width={16} 
                    height={16}
                    style={{ display: 'inline-block' }}
                  />
                  {option.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label>Value</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" />
                Add Field
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56" align="end">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground mb-2">Insert dynamic field</p>
                {dynamicFields.map((field) => (
                  <button
                    key={field.value}
                    onClick={() => handleInsertField(field.value)}
                    className="w-full text-left text-xs p-2 rounded hover:bg-muted transition-colors"
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <Input
          value={block.label}
          onChange={(e) => onChange({ ...block, label: e.target.value })}
          placeholder="email@example.com or {{user_email}}"
        />
      </div>

      <div className="space-y-2">
        <Label>Link Type</Label>
        <Select
          value={block.linkType}
          onValueChange={(value: ContactRowBlockType['linkType']) => onChange({ ...block, linkType: value })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No link</SelectItem>
            <SelectItem value="email">Email (mailto:)</SelectItem>
            <SelectItem value="phone">Phone (tel:)</SelectItem>
            <SelectItem value="url">URL (https://)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Icon Size (px)</Label>
          <Input
            type="number"
            value={block.iconSize}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, iconSize: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, iconSize: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 1) onChange({ ...block, iconSize: 16 });
            }}
          />
        </div>
        <div className="space-y-2">
          <Label>Font Size (px)</Label>
          <Input
            type="number"
            value={block.fontSize}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') onChange({ ...block, fontSize: '' as any });
              else { const num = parseInt(val); if (!isNaN(num)) onChange({ ...block, fontSize: num }); }
            }}
            onBlur={(e) => {
              const num = parseInt(e.target.value);
              if (isNaN(num) || num < 1) onChange({ ...block, fontSize: 13 });
            }}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Text Color</Label>
        <div className="flex gap-2">
          <input
            type="color"
            value={block.textColor}
            onChange={(e) => onChange({ ...block, textColor: e.target.value })}
            className="h-9 w-12 rounded border cursor-pointer"
          />
          <Input
            value={block.textColor}
            onChange={(e) => onChange({ ...block, textColor: e.target.value })}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  );
};
